import { useMutation } from '@tanstack/react-query';
import { CircleHelp, KeyRound, LogOut, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { logout } from '@/features/auth/auth-api';
import { ChangePasswordDialog } from '@/features/auth/change-password-dialog';
import { useAuth } from '@/hooks/use-auth';
import { initials } from '@/lib/format';
import { queryClient } from '@/lib/query-client';
import { cn } from '@/lib/utils';
import { RAIL_ITEM_CLASS } from './rail-styles';

/** Menu do perfil. `rail`: avatar na base da sidebar; `header`: avatar no cabeçalho. */
export function UserMenu({ variant = 'header' }: { variant?: 'header' | 'rail' }) {
  const { user, roles, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const logoutMutation = useMutation({
    mutationFn: logout,
    onSettled: () => {
      queryClient.clear();
      toast.success('Sessão encerrada');
      void navigate('/login', { replace: true });
    },
  });

  if (!user) return null;

  return (
    <>
      <DropdownMenu>
        {variant === 'rail' ? (
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(RAIL_ITEM_CLASS, 'data-[state=open]:bg-sidebar-accent')}
              aria-label={`Perfil de ${user.name}`}
            >
              <Avatar className="size-6">
                <AvatarFallback className="bg-primary-soft text-[10px] font-semibold text-primary">
                  {initials(user.name)}
                </AvatarFallback>
              </Avatar>
              <span className="w-full truncate text-center text-[10.5px] leading-tight font-medium text-muted-foreground">
                Perfil
              </span>
            </button>
          </DropdownMenuTrigger>
        ) : (
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="h-10 gap-2 rounded-lg px-1.5"
              aria-label={`Perfil de ${user.name}`}
            >
              <Avatar>
                <AvatarFallback className="bg-primary-soft text-xs font-semibold text-primary">
                  {initials(user.name)}
                </AvatarFallback>
              </Avatar>
              <span className="hidden max-w-32 truncate text-sm font-medium xl:inline">
                {user.name}
              </span>
            </Button>
          </DropdownMenuTrigger>
        )}
        <DropdownMenuContent
          align="end"
          side={variant === 'rail' ? 'right' : 'bottom'}
          sideOffset={variant === 'rail' ? 10 : 4}
          className="w-60"
        >
          <DropdownMenuLabel className="font-normal">
            <p className="truncate text-sm font-medium">{user.name}</p>
            <p className="truncate text-xs text-muted-foreground">{user.email}</p>
            <p className="mt-1 flex flex-wrap gap-1">
              {isSuperAdmin ? (
                <span className="inline-flex items-center gap-1 text-[11px] text-primary">
                  <ShieldCheck className="size-3" /> Superadmin
                </span>
              ) : null}
              {roles.map((r) => (
                <span
                  key={r.id}
                  className="rounded-sm bg-muted px-1.5 text-[11px] text-muted-foreground"
                >
                  {r.name}
                </span>
              ))}
            </p>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setPasswordOpen(true)}>
            <KeyRound /> Alterar senha
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/ajuda">
              <CircleHelp /> Ajuda e manual
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem variant="destructive" onSelect={() => logoutMutation.mutate()}>
            <LogOut /> Sair
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ChangePasswordDialog open={passwordOpen} onOpenChange={setPasswordOpen} />
    </>
  );
}
